/**
 * ShieldWall — Polymorphic Challenge Generator
 *
 * Generates WCAG 2.2 compliant (SC 3.3.8) challenges that rotate
 * per-session to break static solver logic. No image puzzles.
 *
 * The server controls which challenge types are active via feature
 * flags and provides a seed for deterministic rotation.
 *
 * References:
 *   - Research §7.3: Kasada's polymorphic defense model
 *   - Research §2.3: WCAG 2.2 Accessible Authentication
 *
 * @module challenges
 */

export interface Challenge {
  question: string;
  answer: string;
  inputType: string;
  label: string;
  challengeType: string;
}

type ChallengeGenerator = () => Challenge;

const generators: Record<string, ChallengeGenerator> = {
  /**
   * Simple arithmetic — addition, multiplication, subtraction.
   * Cognitive load: minimal. Accessible: yes (numeric input).
   */
  math: () => {
    const ops = [
      { fn: (a: number, b: number) => a + b, s: "+" },
      { fn: (a: number, b: number) => a * b, s: "×" },
      { fn: (a: number, b: number) => a - b, s: "−" },
    ];
    const op = ops[Math.floor(Math.random() * ops.length)]!;
    const a = Math.floor(Math.random() * 25) + 3;
    const b = Math.floor(Math.random() * 12) + 2;
    // For subtraction, place the larger operand first to guarantee a non-negative result
    const [first, second] = op.s === "−" && b > a ? [b, a] : [a, b];
    return {
      question: `${first} ${op.s} ${second}`,
      answer: String(op.fn(first, second)),
      inputType: "number",
      label: "Solve",
      challengeType: "math",
    };
  },

  /**
   * Word counting — count occurrences of a target word.
   * Cognitive load: low. Accessible: yes (screen-reader friendly).
   */
  word_count: () => {
    const words = [
      "sun", "moon", "star", "tree", "cloud",
      "rain", "fire", "wind", "wave", "snow",
    ];
    const target = words[Math.floor(Math.random() * words.length)];
    const count = Math.floor(Math.random() * 4) + 2;
    const sentence = Array.from(
      { length: 8 },
      () => words[Math.floor(Math.random() * words.length)]
    );

    // Ensure exact target count
    let current = sentence.filter((w) => w === target).length;
    for (let i = 0; i < sentence.length && current !== count; i++) {
      if (current < count && sentence[i] !== target) {
        sentence[i] = target;
        current++;
      } else if (current > count && sentence[i] === target) {
        sentence[i] = words.find((w) => w !== target)!;
        current--;
      }
    }

    return {
      question: `How many times does "${target}" appear: ${sentence.join(" ")}`,
      answer: String(sentence.filter((w) => w === target).length),
      inputType: "number",
      label: "Count",
      challengeType: "word_count",
    };
  },

  /**
   * Number sequence — find the next number in an arithmetic sequence.
   * Cognitive load: low. Accessible: yes (numeric input).
   */
  sequence: () => {
    const start = Math.floor(Math.random() * 10) + 1;
    const step = Math.floor(Math.random() * 5) + 2;
    const seq = Array.from({ length: 4 }, (_, i) => start + step * i);
    return {
      question: `What comes next: ${seq.join(", ")}, ?`,
      answer: String(start + step * 4),
      inputType: "number",
      label: "Next",
      challengeType: "sequence",
    };
  },
};

/**
 * Generate a polymorphic challenge based on a server-issued seed.
 * The seed ensures deterministic type rotation per session.
 *
 * @param seed - Server-provided rotation seed
 * @param enabledTypes - Optional list of enabled challenge types
 * @returns A challenge object with question, expected answer, and metadata
 */
export function generateChallenge(
  seed: number,
  enabledTypes?: string[]
): Challenge {
  const types = enabledTypes || Object.keys(generators);
  const validTypes = types.filter((t) => t in generators);

  if (validTypes.length === 0) {
    throw new Error("No valid challenge types provided");
  }

  const selectedType = validTypes[seed % validTypes.length]!;
  return generators[selectedType]!();
}

/**
 * Verify a challenge answer.
 */
export function verifyChallenge(
  userAnswer: string,
  expectedAnswer: string
): boolean {
  return userAnswer.trim() === expectedAnswer;
}
